import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Paper, Typography, Box, Tooltip, CircularProgress } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
} from 'recharts';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import M from 'messages';
import CustomTooltip from './customTooltip';
import classes from '../styles';

const LineChartWidget = ({ title, data, xAxisLabelFormater, chartKeys, tooltip, tipMessage, loading }) => {
  const [hoveringData, setHoveringData] = useState({});
  const [barProps, setBarProps] = useState(
    data.reduce(
      (a, { key }) => {
        // eslint-disable-next-line no-param-reassign
        a[key] = false;
        return a;
      },
      { hover: null },
    ),
  );

  const handleMouseEnter = payload => {
    chartKeys.forEach(cartItem => {
      if (cartItem.key !== payload.dataKey) {
        hoveringData[cartItem.key] = 0.5;
      } else {
        hoveringData[cartItem.key] = 1;
      }
    });
    setHoveringData(Object.assign({}, hoveringData));
  };

  const handleMouseLeave = () => {
    setHoveringData(Object.assign({}, {}));
  };

  const handleLegendClick = e => {
    setBarProps({
      ...barProps,
      [e.dataKey]: !barProps[e.dataKey],
      hover: null,
    });
  };

  const getInterval = () => {
    let interval = 300;
    if (data.length < 10) {
      interval = 0;
    } else {
      interval = Math.round(data.length / 7);
    }
    return interval;
  };

  return (
    <Paper elevation={2} sx={{ padding: 2, paddingBottom: 2, minHeight: 300 }}>
      <Typography mb={1} display="flex" alignItems="center" sx={classes.chartTitle}>
        {title}
        <Tooltip placement="top" title={tooltip}>
          <HelpOutlineIcon sx={{ marginLeft: '5px', fontSize: '1.3rem' }} />
        </Tooltip>
      </Typography>
      {tipMessage && (
        <Typography variant="body2" mb={2}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
            {M.get('dashboard.lineChart.tip')}
          </Typography>{' '}
          <Typography variant="body2" component="span" sx={classes.chartTipMessage}>
            {tipMessage}
          </Typography>
        </Typography>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : !data.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Typography variant="body2">No data available</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            height: 300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& .recharts-default-legend > li': {
              cursor: 'pointer',
            },
          }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{
                right: 30,
                top: 10,
              }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                interval={getInterval()}
                tickFormatter={xAxisLabelFormater ? value => xAxisLabelFormater(value) : null}
                tickSize={10}
              />
              <YAxis tickSize={10} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleLegendClick} />
              {chartKeys.map(chart => (
                <Fragment key={chart.key}>
                  <Line
                    strokeWidth={2}
                    type="monotone"
                    dataKey={chart.key}
                    stroke={chart.color}
                    strokeOpacity={hoveringData[chart.key]}
                    hide={barProps[chart.key] === true}
                    name={chart.title}
                  />
                </Fragment>
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

LineChartWidget.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  xAxisLabelFormater: PropTypes.func,
  chartKeys: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
  ).isRequired,
  tooltip: PropTypes.string,
  tipMessage: PropTypes.string,
  loading: PropTypes.bool,
};

export default LineChartWidget;
